import {jsPDF} from 'jspdf';
import autoTable from 'jspdf-autotable';
import {Chart as ChartJS} from 'chart.js';
import {MinimalCutSet} from '../minimal-cut-sets';
import {FaultTreeNode} from '../../@types/faultTreeNode';
import {computeInherentRiskScore, DEFAULT_RISK_WEIGHTS, RiskWeights} from '../inherent-risk-score';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChartRefs {
    orderDistribution?: ChartJS | null;
    baseEventFrequency?: ChartJS | null;
    pareto?: ChartJS | null;
    cumulativeCoverage?: ChartJS | null;
    tornado?: ChartJS | null;
    diagnosticImportance?: ChartJS | null;
    inherentRiskGauge?: ChartJS | null;
}

interface VisibleTables {
    importanceMeasures: boolean;
    fmea: boolean;
    coOccurrence: boolean;
}

export interface ReliabilityPdfExportOptions {
    rootNodeName: string;
    cutSets: MinimalCutSet[];
    totalProbability: number;
    totalBaseEvents: number;
    computationTimeMs: number;
    truncated: boolean;
    nodeNameMap: Map<number, string>;
    nodeMap: Map<number, FaultTreeNode>;
    structuralHubIds: Set<number>;
    chartRefs: ChartRefs;
    visibleTables: VisibleTables;
    mcsTopN?: number;
    riskWeights?: RiskWeights;
    showRiskMatrix?: boolean;
    riskMatrixSeverity?: number;
    riskMatrixOperationType?: 'commercial' | 'general';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatImportanceValue(val: number): string {
    if (!isFinite(val)) return 'Inf';
    if (val === 0) return '0';
    if (val >= 1000) return val.toExponential(2);
    if (val >= 10) return val.toFixed(1);
    if (val >= 1) return val.toFixed(2);
    return val.toPrecision(2);
}

function formatProb(value: number): string {
    if (value === 0) return '0';
    if (value < 0.001) return value.toExponential(3);
    return value.toPrecision(4);
}

function addSectionTitle(doc: jsPDF, title: string, y: number, description?: string): number {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, y);
    doc.setFont('helvetica', 'normal');
    y += 6;
    if (description) {
        doc.setFontSize(8);
        doc.setTextColor(100);
        const lines = doc.splitTextToSize(description, doc.internal.pageSize.getWidth() - 28);
        doc.text(lines, 14, y);
        y += lines.length * 3.5 + 2;
        doc.setTextColor(0);
    }
    return y;
}

function addChartImage(doc: jsPDF, chart: ChartJS, title: string, y: number, pageWidth: number, description?: string): number {
    const imgData = chart.toBase64Image('image/png', 1);
    const imgWidth = pageWidth - 28; // 14mm margins each side
    const canvas = chart.canvas;
    const aspectRatio = canvas.height / canvas.width;
    const imgHeight = Math.min(imgWidth * aspectRatio, 120); // Cap height at 120mm

    // Check if we need a new page
    if (y + imgHeight + 12 > doc.internal.pageSize.getHeight() - 10) {
        doc.addPage();
        y = 15;
    }

    y = addSectionTitle(doc, title, y, description);
    doc.addImage(imgData, 'PNG', 14, y, imgWidth, imgHeight);
    return y + imgHeight + 8;
}

// ---------------------------------------------------------------------------
// FAA Risk Matrix (PDF)
// ---------------------------------------------------------------------------

type PdfRiskLevel = 'H' | 'M' | 'L';

const PDF_LIKELIHOOD_ROWS: Array<{label: string; letter: string; minP: number}> = [
    {label: 'Frequent', letter: 'A', minP: 1e-5},
    {label: 'Infrequent', letter: 'B', minP: 1e-7},
    {label: 'Ext. Infrequent', letter: 'C', minP: 1e-8},
    {label: 'Remote', letter: 'D', minP: 1e-9},
    {label: 'Ext. Remote', letter: 'E', minP: 1e-10},
    {label: 'Improbable', letter: 'F', minP: 1e-11},
    {label: 'Ext. Improbable', letter: 'G', minP: 0},
];

const PDF_COMMERCIAL_MATRIX: PdfRiskLevel[][] = [
    ['L', 'M', 'H', 'H', 'H'],
    ['L', 'M', 'H', 'H', 'H'],
    ['L', 'M', 'H', 'H', 'H'],
    ['L', 'M', 'M', 'H', 'H'],
    ['L', 'L', 'M', 'M', 'H'],
    ['L', 'L', 'L', 'M', 'M'],
    ['L', 'L', 'L', 'L', 'L'],
];

const PDF_GENERAL_AVIATION_MATRIX: PdfRiskLevel[][] = [
    ['L', 'M', 'H', 'H', 'H'],
    ['L', 'M', 'M', 'H', 'H'],
    ['L', 'L', 'M', 'M', 'H'],
    ['L', 'L', 'L', 'M', 'M'],
    ['L', 'L', 'L', 'L', 'L'],
    ['L', 'L', 'L', 'L', 'L'],
    ['L', 'L', 'L', 'L', 'L'],
];

const PDF_RISK_MATRICES: Record<string, PdfRiskLevel[][]> = {
    commercial: PDF_COMMERCIAL_MATRIX,
    general: PDF_GENERAL_AVIATION_MATRIX,
};

const PDF_SEVERITY_COLS = ['Minimal', 'Minor', 'Major', 'Hazardous', 'Catastrophic'];

const PDF_RISK_BG: Record<PdfRiskLevel, [number, number, number]> = {
    H: [211, 47, 47], M: [255, 255, 0], L: [76, 175, 80],
};

const PDF_RISK_LABELS: Record<PdfRiskLevel, string> = {H: 'High', M: 'Medium', L: 'Low'};

function getPdfLikelihoodRow(p: number): number {
    for (let i = 0; i < PDF_LIKELIHOOD_ROWS.length; i++) {
        if (p >= PDF_LIKELIHOOD_ROWS[i].minP) return i;
    }
    return PDF_LIKELIHOOD_ROWS.length - 1;
}

function drawRiskMatrix(doc: jsPDF, y: number, activeRow: number, activeCol: number, riskMatrix: PdfRiskLevel[][] = PDF_COMMERCIAL_MATRIX): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const matrixWidth = 120;
    const startX = (pageWidth - matrixWidth) / 2;
    const labelColW = 30;
    const cellW = (matrixWidth - labelColW) / 5;
    const cellH = 6;
    const headerH = 5;

    // "Severity" header
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80);
    doc.text('Severity', startX + labelColW + (5 * cellW) / 2, y, {align: 'center'});
    y += 4;

    // Severity column headers
    doc.setFontSize(6);
    for (let ci = 0; ci < PDF_SEVERITY_COLS.length; ci++) {
        const cx = startX + labelColW + ci * cellW + cellW / 2;
        doc.setFont('helvetica', ci === activeCol ? 'bold' : 'normal');
        doc.setTextColor(ci === activeCol ? 0 : 100);
        doc.text(PDF_SEVERITY_COLS[ci], cx, y, {align: 'center'});
    }
    y += headerH;

    // "Likelihood" vertical label
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80);
    doc.setFontSize(7);
    const matrixBodyH = PDF_LIKELIHOOD_ROWS.length * cellH;
    const lx = startX - 4;
    const ly = y + matrixBodyH / 2;
    doc.text('Likelihood', lx, ly, {angle: 90, align: 'center'});

    // Rows
    doc.setFontSize(6);
    for (let ri = 0; ri < PDF_LIKELIHOOD_ROWS.length; ri++) {
        const row = PDF_LIKELIHOOD_ROWS[ri];
        const ry = y + ri * cellH;

        // Row label
        doc.setFont('helvetica', ri === activeRow ? 'bold' : 'normal');
        doc.setTextColor(ri === activeRow ? 0 : 120);
        doc.text(`${row.letter} – ${row.label}`, startX + 1, ry + cellH / 2 + 1.5);

        // Cells
        for (let ci = 0; ci < 5; ci++) {
            const level = riskMatrix[ri][ci];
            const cx = startX + labelColW + ci * cellW;
            const isActive = ri === activeRow && ci === activeCol;
            const bg = PDF_RISK_BG[level];

            // Fill
            doc.setFillColor(bg[0], bg[1], bg[2]);
            doc.rect(cx, ry, cellW, cellH, 'F');

            // Border
            if (isActive) {
                doc.setDrawColor(0);
                doc.setLineWidth(0.6);
                doc.rect(cx, ry, cellW, cellH, 'S');
                doc.setLineWidth(0.2);

                // Label in active cell
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(0);
                doc.setFontSize(6);
                doc.text(PDF_RISK_LABELS[level], cx + cellW / 2, ry + cellH / 2 + 1.5, {align: 'center'});
            } else {
                doc.setDrawColor(255);
                doc.setLineWidth(0.3);
                doc.rect(cx, ry, cellW, cellH, 'S');
            }
        }
    }

    y += matrixBodyH + 4;

    // Legend
    const legendX = (pageWidth - 80) / 2;
    const swatchW = 6;
    const swatchH = 4;
    const legendSpacing = 28;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80);
    const levels: PdfRiskLevel[] = ['H', 'M', 'L'];
    for (let i = 0; i < levels.length; i++) {
        const lev = levels[i];
        const bg = PDF_RISK_BG[lev];
        const sx = legendX + i * legendSpacing;
        doc.setFillColor(bg[0], bg[1], bg[2]);
        doc.rect(sx, y - 3, swatchW, swatchH, 'F');
        doc.setDrawColor(180);
        doc.setLineWidth(0.2);
        doc.rect(sx, y - 3, swatchW, swatchH, 'S');
        doc.text(`${PDF_RISK_LABELS[lev]} Risk`, sx + swatchW + 1.5, y);
    }

    y += 6;
    doc.setTextColor(0);
    doc.setLineWidth(0.2);

    return y;
}

function ensureSpace(doc: jsPDF, neededMm: number, currentY: number): number {
    if (currentY + neededMm > doc.internal.pageSize.getHeight() - 10) {
        doc.addPage();
        return 15;
    }
    return currentY;
}

// ---------------------------------------------------------------------------
// Importance Measures computation (mirrors ImportanceMeasuresTable logic)
// ---------------------------------------------------------------------------

interface ImportanceRow {
    name: string;
    probability: number;
    cutSetCount: number;
    fv: number;
    raw: number;
    rrw: number;
}

function computeImportanceRows(
    cutSets: MinimalCutSet[],
    nodeMap: Map<number, FaultTreeNode>,
    nodeNameMap: Map<number, string>,
    totalProbability: number,
): ImportanceRow[] {
    if (totalProbability === 0) return [];
    const allIds = new Set<number>();
    cutSets.forEach((cs) => cs.baseEventIds.forEach((id) => allIds.add(id)));

    return Array.from(allIds).map((id) => {
        const node = nodeMap.get(id);
        const prob = node?.probability ?? 0;
        const containingCS = cutSets.filter((cs) => cs.baseEventIds.has(id));
        const containingTotal = containingCS.reduce((s, cs) => s + cs.probability, 0);
        const fv = containingTotal / totalProbability;

        let rawTotal = 0;
        for (const cs of cutSets) {
            if (cs.baseEventIds.has(id)) {
                let p = 1;
                for (const eid of cs.baseEventIds) {
                    if (eid !== id) p *= nodeMap.get(eid)?.probability ?? 0;
                }
                rawTotal += p;
            } else {
                rawTotal += cs.probability;
            }
        }
        const raw = rawTotal / totalProbability;

        const nonContainingTotal = cutSets
            .filter((cs) => !cs.baseEventIds.has(id))
            .reduce((s, cs) => s + cs.probability, 0);
        const rrw = nonContainingTotal > 0 ? totalProbability / nonContainingTotal : Infinity;

        return {
            name: nodeNameMap.get(id) || `ID:${id}`,
            probability: prob,
            cutSetCount: containingCS.length,
            fv,
            raw,
            rrw,
        };
    }).sort((a, b) => b.fv - a.fv);
}

// ---------------------------------------------------------------------------
// FMEA computation (mirrors FMEATable logic)
// ---------------------------------------------------------------------------

interface FMEARow {
    name: string;
    severity: number;
    occurrence: number;
    detection: number;
    rpn: number;
}

function deriveOccurrence(probability: number): number {
    if (probability <= 0) return 1;
    const log = Math.log10(probability);
    const rating = Math.round(((log + 7) / 6) * 9 + 1);
    return Math.max(1, Math.min(10, rating));
}

function deriveSeverity(raw: number): number {
    if (!isFinite(raw) || raw <= 0) return 10;
    if (raw <= 1) return 1;
    const log = Math.log10(raw);
    const rating = Math.round((log / 3) * 9 + 1);
    return Math.max(1, Math.min(10, rating));
}

function deriveDetection(rrw: number): number {
    if (!isFinite(rrw)) return 1;
    if (rrw <= 1) return 10;
    const log = Math.log10(rrw);
    const rating = Math.round(10 - (log / 2) * 9);
    return Math.max(1, Math.min(10, rating));
}

function computeFMEARows(
    cutSets: MinimalCutSet[],
    nodeMap: Map<number, FaultTreeNode>,
    nodeNameMap: Map<number, string>,
    totalProbability: number,
): FMEARow[] {
    if (totalProbability === 0) return [];
    const allIds = new Set<number>();
    cutSets.forEach((cs) => cs.baseEventIds.forEach((id) => allIds.add(id)));

    return Array.from(allIds).map((id) => {
        const node = nodeMap.get(id);
        const prob = node?.probability ?? 0;

        let rawTotal = 0;
        for (const cs of cutSets) {
            if (cs.baseEventIds.has(id)) {
                let p = 1;
                for (const eid of cs.baseEventIds) {
                    if (eid !== id) p *= nodeMap.get(eid)?.probability ?? 0;
                }
                rawTotal += p;
            } else {
                rawTotal += cs.probability;
            }
        }
        const raw = rawTotal / totalProbability;

        const nonContainingTotal = cutSets
            .filter((cs) => !cs.baseEventIds.has(id))
            .reduce((s, cs) => s + cs.probability, 0);
        const rrw = nonContainingTotal > 0 ? totalProbability / nonContainingTotal : Infinity;

        const severity = deriveSeverity(raw);
        const occurrence = deriveOccurrence(prob);
        const detection = deriveDetection(rrw);

        return {
            name: nodeNameMap.get(id) || `ID:${id}`,
            severity,
            occurrence,
            detection,
            rpn: severity * occurrence * detection,
        };
    }).sort((a, b) => b.rpn - a.rpn);
}

// ---------------------------------------------------------------------------
// Executive Summary
// ---------------------------------------------------------------------------

function addExecutiveSummary(
    doc: jsPDF,
    startY: number,
    cutSets: MinimalCutSet[],
    totalProbability: number,
    totalBaseEvents: number,
    computationTimeMs: number,
    truncated: boolean,
    nodeNameMap: Map<number, string>,
    nodeMap: Map<number, FaultTreeNode>,
    structuralHubIds: Set<number>,
    riskWeights?: RiskWeights,
): number {
    let y = startY;
    const pageWidth = doc.internal.pageSize.getWidth();
    const colWidth = (pageWidth - 28) / 2; // Two-column layout
    const leftX = 14;
    const rightX = 14 + colWidth + 6;

    // ── Section title ──
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Executive Summary', leftX, y);
    y += 7;

    // ── Overview stats line ──
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(
        `${cutSets.length} cut set${cutSets.length !== 1 ? 's' : ''}${truncated ? ' (truncated)' : ''}  |`
        + `${totalBaseEvents} basic event${totalBaseEvents !== 1 ? 's' : ''}  |`
        + `P(top event) = ${formatProb(totalProbability)}  |`
        + `Computed in ${computationTimeMs.toFixed(1)} ms`,
        leftX, y,
    );
    y += 6;

    // ── Compute all findings ──
    const spfCutSets = cutSets.filter((cs) => cs.baseEventIds.size === 1);
    const spfCount = spfCutSets.length;
    const hubCount = structuralHubIds.size;

    // Top contributors by probability
    const sorted = [...cutSets].sort((a, b) => b.probability - a.probability);
    let cumSum = 0;
    let cutSetsAt80 = 0;
    let cutSetsAt95 = 0;
    for (let i = 0; i < sorted.length; i++) {
        cumSum += sorted[i].probability;
        const pct = totalProbability > 0 ? (cumSum / totalProbability) * 100 : 0;
        if (cutSetsAt80 === 0 && pct >= 80) cutSetsAt80 = i + 1;
        if (cutSetsAt95 === 0 && pct >= 95) cutSetsAt95 = i + 1;
    }

    // Top 3 base events by FV importance
    const impRows = totalProbability > 0
        ? computeImportanceRows(cutSets, nodeMap, nodeNameMap, totalProbability)
        : [];
    const topByFV = impRows.slice(0, 3);

    // Top 3 base events by RAW
    const topByRAW = [...impRows].sort((a, b) => b.raw - a.raw).slice(0, 3);

    // Order distribution
    const orderCounts = new Map<number, number>();
    cutSets.forEach((cs) => orderCounts.set(cs.order, (orderCounts.get(cs.order) || 0) + 1));
    const minOrder = Math.min(...orderCounts.keys());
    const maxOrder = Math.max(...orderCounts.keys());

    // ── Left column: Key Findings ──
    const findingsX = leftX;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Key Findings', findingsX, y);
    let ly = y + 5;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');

    const bullet = (text: string, color?: [number, number, number]): void => {
        if (color) doc.setTextColor(...color);
        doc.text(`-  ${text}`, findingsX + 2, ly);
        if (color) doc.setTextColor(0);
        ly += 4;
    };

    // Structural Vulnerability Score
    const weights = riskWeights ?? DEFAULT_RISK_WEIGHTS;
    const riskScore = computeInherentRiskScore(cutSets, totalProbability, totalBaseEvents, structuralHubIds, weights);
    const riskColorRgb: [number, number, number] = riskScore.riskZone === 'Critical' ? [211, 47, 47]
        : riskScore.riskZone === 'High' ? [237, 108, 2]
            : riskScore.riskZone === 'Moderate' ? [200, 160, 0]
                : [76, 175, 80];
    bullet(`Structural Vulnerability: ${Math.round(riskScore.composite)}/100 (${riskScore.riskZone})`, riskColorRgb);

    // FAA Risk Matrix likelihood
    const summaryRow = getPdfLikelihoodRow(totalProbability);
    const summaryLikelihood = PDF_LIKELIHOOD_ROWS[summaryRow];
    bullet(`FAA Likelihood: ${summaryLikelihood.label} (${summaryLikelihood.letter}) — severity requires analyst assessment`);

    // Annotate weights used for the risk score
    const wLabel = `Weights: P(top) ${(weights.logScore * 100).toFixed(0)}%, `
        + `SPF ${(weights.spfPenalty * 100).toFixed(0)}%, `
        + `Hub ${(weights.hubPenalty * 100).toFixed(0)}%, `
        + `Low-Order ${(weights.orderConcentration * 100).toFixed(0)}%`;
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.text(`  ${wLabel}`, findingsX + 6, ly);
    ly += 4;
    doc.setFontSize(8);
    doc.setTextColor(0);

    // SPFs
    if (spfCount > 0) {
        const spfNames = spfCutSets
            .sort((a, b) => b.probability - a.probability)
            .slice(0, 3)
            .map((cs) => {
                const id = Array.from(cs.baseEventIds)[0];
                return nodeNameMap.get(id) || `ID:${id}`;
            });
        bullet(`${spfCount} single point${spfCount !== 1 ? 's' : ''} of failure found -- no redundancy protects against these events`, [211, 47, 47]);
        const spfLabel = spfNames.length < spfCount
            ? `Top SPFs: ${spfNames.join(', ')} (+${spfCount - spfNames.length} more)`
            : `SPFs: ${spfNames.join(', ')}`;
        const spfLines = doc.splitTextToSize(spfLabel, colWidth - 10);
        doc.setFontSize(7);
        doc.setTextColor(100);
        doc.text(spfLines, findingsX + 6, ly);
        ly += spfLines.length * 3 + 1;
        doc.setFontSize(8);
        doc.setTextColor(0);
    } else {
        bullet('No single points of failure -- all failure paths require multiple events', [46, 125, 50]);
    }

    // Structural hubs
    if (hubCount > 0) {
        const hubNames = Array.from(structuralHubIds)
            .slice(0, 3)
            .map((id) => nodeNameMap.get(id) || `ID:${id}`);
        const hubLabel = hubNames.length < hubCount
            ? `${hubNames.join(', ')} (+${hubCount - hubNames.length} more)`
            : hubNames.join(', ');
        bullet(`${hubCount} structural hub${hubCount !== 1 ? 's' : ''} -- event${hubCount !== 1 ? 's' : ''} appearing in >=50% of cut sets`, [237, 108, 2]);
        const hubLines = doc.splitTextToSize(hubLabel, colWidth - 10);
        doc.setFontSize(7);
        doc.setTextColor(100);
        doc.text(hubLines, findingsX + 6, ly);
        ly += hubLines.length * 3 + 1;
        doc.setFontSize(8);
        doc.setTextColor(0);
    }

    // Risk concentration
    if (totalProbability > 0 && cutSetsAt80 > 0) {
        const pctOf80 = ((cutSetsAt80 / cutSets.length) * 100).toFixed(0);
        bullet(`Risk is ${cutSetsAt80 <= 5 ? 'highly concentrated' : cutSetsAt80 <= 20 ? 'moderately concentrated' : 'widely distributed'}: ${cutSetsAt80} of ${cutSets.length} cut sets (${pctOf80}%) account for 80% of risk`);
        if (cutSetsAt95 > 0) {
            bullet(`${cutSetsAt95} cut sets account for 95% of total probability`);
        }
    }

    // Order range
    bullet(`Cut set orders range from ${minOrder} to ${maxOrder} (${orderCounts.size} distinct order${orderCounts.size !== 1 ? 's' : ''})`);

    // ── Right column: Top Contributors ──
    const contribX = rightX;
    let ry = y;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Top Risk Contributors', contribX, ry);
    ry += 5;

    if (topByFV.length > 0) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('By Risk Contribution (fraction of risk involving this event):', contribX + 2, ry);
        ry += 4;
        doc.setFont('helvetica', 'normal');
        for (let i = 0; i < topByFV.length; i++) {
            const r = topByFV[i];
            const line = `${i + 1}. ${r.name} -- RC: ${(r.fv * 100).toFixed(1)}%, in ${r.cutSetCount} cut set${r.cutSetCount !== 1 ? 's' : ''}`;
            const lines = doc.splitTextToSize(line, colWidth - 6);
            doc.text(lines, contribX + 4, ry);
            ry += lines.length * 3.5;
        }
        ry += 2;
    }

    if (topByRAW.length > 0) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('By Risk Increase Factor (criticality if failed):', contribX + 2, ry);
        ry += 4;
        doc.setFont('helvetica', 'normal');
        for (let i = 0; i < topByRAW.length; i++) {
            const r = topByRAW[i];
            const rawStr = isFinite(r.raw) ? formatImportanceValue(r.raw) : 'Inf';
            const line = `${i + 1}. ${r.name} -- RIF: ${rawStr}, P = ${formatProb(r.probability)}`;
            const lines = doc.splitTextToSize(line, colWidth - 6);
            doc.text(lines, contribX + 4, ry);
            ry += lines.length * 3.5;
        }
        ry += 2;
    }

    // Top 3 cut sets
    if (sorted.length > 0 && totalProbability > 0) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Top cut sets by probability:', contribX + 2, ry);
        ry += 4;
        doc.setFont('helvetica', 'normal');
        const topCS = sorted.slice(0, 3);
        for (let i = 0; i < topCS.length; i++) {
            const cs = topCS[i];
            const pct = ((cs.probability / totalProbability) * 100).toFixed(1);
            const names = Array.from(cs.baseEventIds).map((id) => nodeNameMap.get(id) || `ID:${id}`).join(', ');
            const line = `${i + 1}. [${pct}%] ${names}`;
            const lines = doc.splitTextToSize(line, colWidth - 6);
            doc.text(lines, contribX + 4, ry);
            ry += lines.length * 3.5;
        }
    }

    // Use the taller of the two columns
    y = Math.max(ly, ry) + 6;

    // Divider line
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.line(leftX, y, pageWidth - 14, y);
    y += 6;

    return y;
}

// ---------------------------------------------------------------------------
// Main export function
// ---------------------------------------------------------------------------

export function exportReliabilityPdf(options: ReliabilityPdfExportOptions): void {
    const {
        rootNodeName, cutSets, totalProbability, totalBaseEvents,
        computationTimeMs, truncated, nodeNameMap, nodeMap,
        structuralHubIds, chartRefs, visibleTables,
        mcsTopN = 20,
        riskWeights,
        showRiskMatrix = false,
        riskMatrixSeverity = -1,
        riskMatrixOperationType = 'commercial',
    } = options;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 15;

    // ── Header ──
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Reliability Analysis -- ${rootNodeName}`, 14, y);
    y += 7;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    // ── Executive Summary ──
    y = addExecutiveSummary(doc, y, cutSets, totalProbability, totalBaseEvents,
        computationTimeMs, truncated, nodeNameMap, nodeMap, structuralHubIds, riskWeights);

    // ── Inherent Risk Gauge ──
    if (chartRefs.inherentRiskGauge) {
        const gaugeDesc = 'Composite structural vulnerability score derived from cut set analysis';
        // Render gauge at a fixed width (centered) to preserve its compact aspect ratio
        const gaugeChart = chartRefs.inherentRiskGauge;
        const gaugeImgData = gaugeChart.toBase64Image('image/png', 1);
        const gaugeAspect = gaugeChart.canvas.height / gaugeChart.canvas.width;
        const gaugeImgWidth = 100; // mm — compact size, not full page width
        const gaugeImgHeight = gaugeImgWidth * gaugeAspect;
        const gaugeX = (pageWidth - gaugeImgWidth) / 2; // center horizontally

        if (y + gaugeImgHeight + 12 > doc.internal.pageSize.getHeight() - 10) {
            doc.addPage();
            y = 15;
        }
        y = addSectionTitle(doc, 'Structural Vulnerability', y, gaugeDesc);
        doc.addImage(gaugeImgData, 'PNG', gaugeX, y, gaugeImgWidth, gaugeImgHeight);
        y += gaugeImgHeight + 8;
    }

    // ── FAA Risk Matrix ──
    if (showRiskMatrix) {
        const matrixActiveRow = getPdfLikelihoodRow(totalProbability);
        const matrixActiveCol = riskMatrixSeverity >= 0 ? riskMatrixSeverity : -1;
        const pdfMatrix = PDF_RISK_MATRICES[riskMatrixOperationType] ?? PDF_COMMERCIAL_MATRIX;
        const opsLabel = riskMatrixOperationType === 'general' ? 'General Aviation' : 'Commercial Aviation';
        y = ensureSpace(doc, 65, y);
        y = addSectionTitle(doc, `Risk Matrix (FAA Order 8040.4C) — ${opsLabel}`, y,
            'Likelihood is derived from P(top event). Severity requires analyst assessment based on consequence analysis.');
        y = drawRiskMatrix(doc, y, matrixActiveRow, matrixActiveCol, pdfMatrix);
    }

    // ── Charts ──
    const chartEntries: Array<[string, ChartJS | null | undefined, string]> = [
        ['Order Distribution', chartRefs.orderDistribution,
            'How many cut sets exist at each order (number of simultaneous failures required)? Highlights whether single points of failure dominate or risk is spread across higher-order combinations.'],
        ['Basic Event Frequency', chartRefs.baseEventFrequency,
            'Which base events appear most often across all cut sets? Events with high frequency participate in many failure paths and may represent systemic vulnerabilities.'],
        ['Pareto Analysis', chartRefs.pareto,
            'Which individual cut sets contribute the most to the top event probability? Identifies the vital few cut sets that dominate overall risk, following the 80/20 principle.'],
        ['Cumulative Coverage by Cut Set Order', chartRefs.cumulativeCoverage,
            'What fraction of the total top event probability is accounted for by each cut set order? Helps determine whether higher-order cut sets can be safely deprioritized.'],
        ['Tornado Chart', chartRefs.tornado,
            'How sensitive is the top event probability to changes in each base event? Shows the effect of doubling or halving each event\'s probability, ranked by total swing.'],
        ['Failure Attribution', chartRefs.diagnosticImportance,
            'Given that the top event has occurred, what is the probability that each base event was a contributing cause? Derived from Risk Contribution measure, normalized so contributions sum to 100%.'],
    ];

    for (const [title, chart, description] of chartEntries) {
        if (chart) {
            y = addChartImage(doc, chart, title, y, pageWidth, description);
        }
    }

    // ── MCS Table (top N) ──
    if (mcsTopN > 0) {
        y = ensureSpace(doc, 40, y);
        const topN = Math.min(mcsTopN, cutSets.length);
        const sorted = [...cutSets]
            .map((cs, i) => ({ index: i + 1, cs }))
            .sort((a, b) => b.cs.probability - a.cs.probability)
            .slice(0, topN);

        const mcsTitle = topN >= cutSets.length
            ? `Minimal Cut Sets -- All ${cutSets.length}`
            : `Minimal Cut Sets -- Top ${topN} by Contribution`;
        y = addSectionTitle(doc, mcsTitle, y,
            'Each cut set is a minimal combination of base event failures that can cause the top event. Ranked by contribution to the total top event probability.');

        autoTable(doc, {
            startY: y,
            head: [['#', 'Order', 'Probability', 'Contribution %', 'Basic Events']],
            body: sorted.map((row) => [
                row.index,
                row.cs.order,
                formatProb(row.cs.probability),
                totalProbability > 0 ? `${(row.cs.probability / totalProbability * 100).toFixed(1)}%` : '-',
                Array.from(row.cs.baseEventIds).map((id) => nodeNameMap.get(id) || `ID:${id}`).join(', '),
            ]),
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 7 },
            columnStyles: {
                0: { cellWidth: 12 },
                1: { cellWidth: 14 },
                2: { cellWidth: 25 },
                3: { cellWidth: 25 },
                4: { cellWidth: 'auto' },
            },
            margin: { left: 14, right: 14 },
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        y = (doc as any).lastAutoTable.finalY + 8;
    }

    // ── Importance Measures Table ──
    if (visibleTables.importanceMeasures) {
        y = ensureSpace(doc, 40, y);
        y = addSectionTitle(doc, 'Risk Significance', y,
            'Quantifies each base event\'s contribution to risk. RC (Risk Contribution): fraction of top event probability involving this event. RIF (Risk Increase Factor): risk increase if the event is guaranteed to fail. RRF (Risk Reduction Factor): risk decrease if the event is made perfectly reliable.');

        const impRows = computeImportanceRows(cutSets, nodeMap, nodeNameMap, totalProbability);
        autoTable(doc, {
            startY: y,
            head: [['Basic Event', 'Probability', 'Cut Sets', 'RC %', 'RIF', 'RRF']],
            body: impRows.map((r) => [
                r.name,
                formatProb(r.probability),
                r.cutSetCount,
                (r.fv * 100).toFixed(1) + '%',
                formatImportanceValue(r.raw),
                formatImportanceValue(r.rrw),
            ]),
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 7 },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            didParseCell: (data: any): void => {
                if (data.section !== 'body') return;
                const rowIdx = data.row.index as number;
                const colIdx = data.column.index as number;
                const row = impRows[rowIdx];
                if (!row) return;

                // FV column (index 3) --red/orange scale
                if (colIdx === 3) {
                    const fv = row.fv;
                    if (fv >= 0.8) { data.cell.styles.fillColor = [211, 47, 47]; data.cell.styles.textColor = 255; }
                    else if (fv >= 0.5) { data.cell.styles.fillColor = [233, 151, 151]; }
                    else if (fv >= 0.2) { data.cell.styles.fillColor = [255, 213, 153]; }
                }
                // RAW column (index 4) --red/orange scale
                if (colIdx === 4) {
                    const raw = row.raw;
                    if (raw >= 100) { data.cell.styles.fillColor = [211, 47, 47]; data.cell.styles.textColor = 255; }
                    else if (raw >= 10) { data.cell.styles.fillColor = [233, 151, 151]; }
                    else if (raw >= 2) { data.cell.styles.fillColor = [255, 213, 153]; }
                }
                // RRW column (index 5) --green scale
                if (colIdx === 5) {
                    const rrw = row.rrw;
                    if (rrw >= 10) { data.cell.styles.fillColor = [46, 125, 50]; data.cell.styles.textColor = 255; }
                    else if (rrw >= 2) { data.cell.styles.fillColor = [129, 189, 131]; }
                    else if (rrw >= 1.1) { data.cell.styles.fillColor = [192, 224, 193]; }
                }
            },
            margin: { left: 14, right: 14 },
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        y = (doc as any).lastAutoTable.finalY + 8;
    }

    // ── FMEA / Criticality Analysis Table ──
    if (visibleTables.fmea) {
        y = ensureSpace(doc, 40, y);
        y = addSectionTitle(doc, 'Criticality Analysis (S/O/D/RPN)', y,
            'FMEA-style risk prioritization. Severity (from RIF): impact if the event occurs. Occurrence: likelihood rating from base event probability. Detection (from inverse RRF): difficulty of mitigating the event. RPN = S x O x D; higher values indicate priority for corrective action.');

        const fmeaRows = computeFMEARows(cutSets, nodeMap, nodeNameMap, totalProbability);
        autoTable(doc, {
            startY: y,
            head: [['Basic Event', 'Severity', 'Occurrence', 'Detection', 'RPN']],
            body: fmeaRows.map((r) => [r.name, r.severity, r.occurrence, r.detection, r.rpn]),
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 7 },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            didParseCell: (data: any): void => {
                if (data.section !== 'body') return;
                const colIdx = data.column.index as number;
                // Severity / Occurrence / Detection (columns 1–3) --rating color scale
                if (colIdx >= 1 && colIdx <= 3) {
                    const val = data.row.raw[colIdx] as number;
                    if (val >= 8) { data.cell.styles.fillColor = [211, 47, 47]; data.cell.styles.textColor = 255; }
                    else if (val >= 5) { data.cell.styles.fillColor = [255, 200, 150]; }
                    else if (val >= 3) { data.cell.styles.fillColor = [255, 243, 176]; }
                }
                // RPN (column 4)
                if (colIdx === 4) {
                    const rpn = data.row.raw[4] as number;
                    if (rpn >= 200) { data.cell.styles.fillColor = [211, 47, 47]; data.cell.styles.textColor = 255; }
                    else if (rpn >= 100) { data.cell.styles.fillColor = [239, 154, 154]; }
                    else if (rpn >= 50) { data.cell.styles.fillColor = [255, 224, 178]; }
                }
            },
            margin: { left: 14, right: 14 },
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        y = (doc as any).lastAutoTable.finalY + 8;
    }

    // ── Co-Occurrence Matrix ──
    if (visibleTables.coOccurrence) {
        // Build co-occurrence matrix --skip entirely if too large for PDF
        const allIds = new Set<number>();
        cutSets.forEach((cs) => { if (cs.order >= 2) cs.baseEventIds.forEach((id) => allIds.add(id)); });
        const ids = Array.from(allIds);

        if (ids.length > 0 && ids.length <= 50) {
            y = ensureSpace(doc, 40, y);
            y = addSectionTitle(doc, 'Double Failure Matrix', y,
                'How often do pairs of base events appear together in the same cut set? High co-occurrence suggests correlated failure modes or common cause potential.');

            const names = ids.map((id) => nodeNameMap.get(id) || `ID:${id}`);
            const matrix = new Map<string, number>();
            for (const cs of cutSets) {
                const csIds = Array.from(cs.baseEventIds);
                for (let i = 0; i < csIds.length; i++) {
                    for (let j = i; j < csIds.length; j++) {
                        const key = `${csIds[i]}-${csIds[j]}`;
                        matrix.set(key, (matrix.get(key) || 0) + 1);
                    }
                }
            }

            autoTable(doc, {
                startY: y,
                head: [['', ...names]],
                body: ids.map((rowId, ri) =>
                    [names[ri], ...ids.map((colId, ci) => {
                        if (ci < ri) return '';
                        const key = ri <= ci ? `${rowId}-${colId}` : `${colId}-${rowId}`;
                        return matrix.get(key) || 0;
                    })],
                ),
                styles: { fontSize: 5, cellPadding: 1, halign: 'center' },
                headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 5 },
                margin: { left: 14, right: 14 },
            });
        }
    }

    // ── Save ──
    const safeName = rootNodeName.replace(/[^a-zA-Z0-9_-]/g, '_');
    doc.save(`reliability_analysis_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
